// Tee picker helpers — disambiguate same-color tees with different gender
// ratings (e.g. Blue 72.1/130 men vs Blue 77.5/138 women). The DB carries
// two course_tees rows per physical tee box (UNIQUE constraint widened in
// PR #95 to (course_id, tee_name, gender)); the picker needs a composite
// key + label so they're distinguishable.
//
// Key format: `<tee_name>|<gender>` (e.g. 'Blue|women'). Stored in
// state and persisted through standaloneState.tee. Backwards-compat:
// findTeeByKey falls back to bare-name match when the value doesn't
// contain a pipe (old saved rounds carry just the tee_name string).

export function teeKey(t) {
  if (!t) return null
  return `${t.tee_name}|${t.gender || 'unisex'}`
}

// Display suffix for the gender variant. Unisex tees (no gender) get no
// suffix; men/women get a compact " · M" / " · W" so the dropdown stays
// readable.
export function teeGenderSuffix(t) {
  if (t?.gender === 'women') return 'W'
  if (t?.gender === 'men') return 'M'
  return null
}

// Strip the gender from a composite key (or return the bare value if
// already a plain name). Used when looking up yardages that ARE keyed by
// tee_name regardless of gender — same physical tee box, same yards.
export function teeNameOf(key) {
  if (!key) return null
  const i = String(key).indexOf('|')
  return i === -1 ? String(key) : String(key).slice(0, i)
}

// Find the matching tee row given a composite key OR a bare tee_name
// (legacy). Bare-name match returns the first found — gender ambiguity
// only matters for newly-saved rounds where the picker writes a composite.
export function findTeeByKey(tees, key) {
  if (!key || !Array.isArray(tees)) return null
  if (String(key).includes('|')) return tees.find((t) => teeKey(t) === key) || null
  return tees.find((t) => t.tee_name === key) || null
}

// Per-player tee (T3). PlayPage stamps each seat's chosen tee onto
// activeRound.players[].tee (a composite key). ScorecardPage's save loop
// resolves each round_scores row's tee from THAT seat's key, falling back
// to the round-level default when a seat carries none (legacy rounds or an
// un-overridden seat). Centralised here so the write site and the handicap
// path resolve identically.
//
//   seatTee  — the seat's own tee key (may be null/undefined)
//   roundTee — the round-level default tee key (standaloneState.tee)
export function resolveSeatTeeKey(seatTee, roundTee) {
  return seatTee || roundTee || null
}

// Resolve a tee ROW for a seat: prefer the seat's own key, then the round
// default, then the course's first tee (matches the pre-T3 fallback so a
// keyless legacy round still resolves to a real row). `tees` is the course's
// tee list.
export function resolveSeatTee(tees, seatTee, roundTee) {
  const list = Array.isArray(tees) ? tees : null
  if (!list) return null
  const key = resolveSeatTeeKey(seatTee, roundTee)
  return (key ? findTeeByKey(list, key) : null) || list[0] || null
}

// Per-player YARDAGE (T3 display). Resolve a hole's yardage for a seat from an
// all-tees map (`hole.all_yardages`, built from course.holes_data.yardages —
// keyed by bare tee_name, since men/women variants of the same physical tee
// share yards). Prefers the seat's own tee, then the round default. Returns
// null when neither maps to a known yardage, so callers can fall back to the
// shared hole yardage. PAR is NOT resolved here — par is per-hole/course-level
// (identical across tees) and stays shared; only yardage is per-tee.
//
//   allYardages — { [tee_name]: yards } map for a single hole
//   seatTee     — the seat's own tee key (composite `Blue|women` or bare name)
//   roundTee    — the round-level default tee key
export function resolveSeatYardage(allYardages, seatTee, roundTee) {
  if (!allYardages || typeof allYardages !== 'object') return null
  const key = resolveSeatTeeKey(seatTee, roundTee)
  if (!key) return null
  const y = allYardages[teeNameOf(key)]
  return y == null ? null : y
}

// The distinct set of tee NAMES a group is actually playing (rework v2 —
// bold-in-play hole yardage list). `seatTees` is the per-seat tee keys (an
// array, or a slot→key map); each seat that carries no own key falls back to
// `roundTee`, the round-level default. Returns a Set of BARE tee_names (gender
// stripped, since the per-hole yardage list keys on bare name). Only meaningful
// for standalone rounds where seats carry per-seat tees; when no seat resolves
// but a round default exists, that default is the sole tee in play.
export function teesInPlay(seatTees, roundTee) {
  const out = new Set()
  const vals = Array.isArray(seatTees)
    ? seatTees
    : (seatTees && typeof seatTees === 'object') ? Object.values(seatTees) : []
  for (const k of vals) {
    const name = teeNameOf(resolveSeatTeeKey(k, roundTee))
    if (name) out.add(name)
  }
  if (out.size === 0 && roundTee) {
    const n = teeNameOf(roundTee)
    if (n) out.add(n)
  }
  return out
}
